/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import {
  ActivityIndicator,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { OperationalConfirmBody } from '../../shared/modals/OperationalConfirmModal';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { spacing } from '../../shared/theme/spacing'
import { ticketFonts } from '../../shared/theme/ticketColors';
import { ticketColors } from '../../shared/theme/ticketColors';
import type { CloseBusinessReason } from '../../auth/application/authSession';

export type CloseBusinessConfirmModalProps = {
  visible: boolean;
  onClose: () => void;
  submitting: boolean;
  onConfirmClose: () => void | Promise<void>;
  selectedReason: CloseBusinessReason | null;
  onSelectReason: (reason: CloseBusinessReason) => void;
  /** Página web con instrucciones y contacto si no pueden usar la app. */
  accountDeletionHelpUrl?: string;
};

const CLOSE_REASON_OPTIONS: ReadonlyArray<{ value: CloseBusinessReason; label: string }> = [
  { value: 'missing_features', label: 'Me faltan funciones o herramientas' },
  { value: 'product_not_fit', label: 'Vuelto no se adapta a lo que necesito' },
  { value: 'technical_issues', label: 'Tuve problemas técnicos' },
  { value: 'too_expensive', label: 'El costo no me cierra' },
  { value: 'low_sales', label: 'Tengo pocas ventas' },
  { value: 'switched_tool', label: 'Me pasé a otra herramienta' },
  { value: 'no_longer_operating', label: 'Ya no tengo el negocio' },
  { value: 'temporary_pause', label: 'Estoy pausando por un tiempo' },
  { value: 'support_experience', label: 'Tuve una mala experiencia con el soporte' },
  { value: 'other', label: 'Otro motivo' },
];

const CONFIRM_WARN = '#B45309';

/**
 * Dos pasos, mismo look Square que OperationalConfirmModal (paper / soft cancel / warn).
 */
export function CloseBusinessConfirmModal({
  visible,
  onClose,
  submitting,
  onConfirmClose,
  selectedReason,
  onSelectReason,
  accountDeletionHelpUrl,
}: CloseBusinessConfirmModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const modalBackdropPadding = useModalBackdropPadding();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const isMobile = Platform.OS !== 'web';
  const narrow = width < 420;
  const useSheet = isMobile || narrow;

  useEffect(() => {
    if (visible) setStep(1);
  }, [visible]);

  const messageStep2 =
    'Vas a cerrar tu negocio en Vuelto. Si tenés caja abierta, pagos a riders o reclamos recientes, resolvelos antes. Podés reabrir cuando quieras.';

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const title = step === 1 ? 'Cerrar negocio' : 'Última confirmación';

  const helpLink =
    step === 1 && accountDeletionHelpUrl ? (
      <Text style={styles.helpLinkWrap}>
        <Text style={styles.helpHint}>¿No podés hacerlo desde acá? </Text>
        <Text
          style={styles.helpLink}
          onPress={() => void Linking.openURL(accountDeletionHelpUrl)}
          accessibilityRole="link"
        >
          Más info
        </Text>
      </Text>
    ) : null;

  const footer =
    step === 1 ? (
      <View style={styles.footer}>
        <TouchableOpacity
          style={styles.btnCancel}
          onPress={handleClose}
          disabled={submitting}
          accessibilityRole="button"
        >
          <Text style={styles.btnCancelText} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.8}>
            Cancelar
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.btnDanger, (!selectedReason || submitting) && styles.btnDisabled]}
          onPress={() => setStep(2)}
          disabled={submitting || !selectedReason}
          accessibilityRole="button"
        >
          <Text style={styles.btnDangerText} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.8}>
            Continuar
          </Text>
        </TouchableOpacity>
      </View>
    ) : (
      <View style={styles.footer}>
        <TouchableOpacity
          style={styles.btnCancel}
          onPress={() => setStep(1)}
          disabled={submitting}
          accessibilityRole="button"
        >
          <Text style={styles.btnCancelText} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.8}>
            No, volver
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.btnDanger, submitting && styles.btnDisabled]}
          onPress={() => void onConfirmClose()}
          disabled={submitting}
          accessibilityRole="button"
        >
          {submitting ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.btnDangerText} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.8}>
              Sí, cerrar negocio
            </Text>
          )}
        </TouchableOpacity>
      </View>
    );

  const body = (
    <>
      {step === 1 ? (
        <>
          <OperationalConfirmBody
            message="Antes de cerrar, contanos por qué. Nos ayuda a mejorar Vuelto."
            destructive
            iconName="chatbubble-ellipses-outline"
            cleanDesign
          />
          <View style={styles.reasonList}>
            {CLOSE_REASON_OPTIONS.map((option) => {
              const selected = selectedReason === option.value;
              return (
                <Pressable
                  key={option.value}
                  style={[styles.reasonRow, selected && styles.reasonRowSelected]}
                  onPress={() => onSelectReason(option.value)}
                  disabled={submitting}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: selected }}
                  accessibilityLabel={option.label}
                >
                  <Ionicons
                    name={selected ? 'radio-button-on' : 'radio-button-off'}
                    size={21}
                    color={selected ? ticketColors.gold : ticketColors.ink3}
                  />
                  <Text style={styles.reasonLabel}>{option.label}</Text>
                </Pressable>
              );
            })}
          </View>
          {helpLink}
        </>
      ) : (
        <OperationalConfirmBody message={messageStep2} destructive iconName="warning-outline" cleanDesign />
      )}
    </>
  );

  if (useSheet) {
    return (
      <Modal visible={visible} transparent animationType="slide" onRequestClose={handleClose}>
        <View style={styles.sheetBackdrop}>
          <Pressable style={StyleSheet.absoluteFill} onPress={handleClose} accessibilityRole="button" accessibilityLabel="Cerrar" />
          <View
            style={[styles.sheet, { paddingBottom: Math.max(insets.bottom, spacing.sm) }]}
            onStartShouldSetResponder={() => true}
          >
            <View style={styles.grabber} />
            <View style={styles.header}>
              <Text style={styles.title}>{title}</Text>
              <TouchableOpacity
                onPress={handleClose}
                style={styles.closeBtn}
                activeOpacity={0.6}
                accessibilityRole="button"
                accessibilityLabel="Cerrar"
              >
                <Ionicons name="close" size={16} color={ticketColors.ink3} />
              </TouchableOpacity>
            </View>
            <ScrollView
              style={styles.scroll}
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={false}
            >
              {body}
            </ScrollView>
            {footer}
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <View style={[styles.deskBackdrop, modalBackdropPadding]}>
        <Pressable style={StyleSheet.absoluteFill} onPress={handleClose} accessibilityRole="button" accessibilityLabel="Cerrar" />
        <View style={styles.deskWrap} pointerEvents="box-none">
          <View style={styles.deskCard} onStartShouldSetResponder={() => true}>
            <View style={styles.header}>
              <Text style={styles.title}>{title}</Text>
              <TouchableOpacity
                onPress={handleClose}
                style={styles.closeBtn}
                activeOpacity={0.6}
                accessibilityRole="button"
                accessibilityLabel="Cerrar"
              >
                <Ionicons name="close" size={16} color={ticketColors.ink3} />
              </TouchableOpacity>
            </View>
            <ScrollView
              style={styles.deskScroll}
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={false}
            >
              {body}
            </ScrollView>
            {footer}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  sheetBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(15,20,18,0.45)',
    justifyContent: 'flex-end',
  },
  deskBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(15,20,18,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  deskWrap: {
    width: '100%',
    maxWidth: 420,
    zIndex: 1,
  },
  deskCard: {
    backgroundColor: ticketColors.paper,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    overflow: 'hidden',
    maxHeight: '90%',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 12px 40px rgba(15,20,18,0.12)' } as const)
      : {
          shadowColor: '#0F1412',
          shadowOpacity: 0.12,
          shadowRadius: 20,
          shadowOffset: { width: 0, height: 8 },
          elevation: 8,
        }),
  },
  sheet: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    maxHeight: '85%',
    width: '100%',
    zIndex: 1,
  },
  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(28,25,23,0.14)',
    alignSelf: 'center',
    marginTop: 4,
    marginBottom: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    paddingHorizontal: 18,
    paddingTop: Platform.OS === 'web' ? 16 : 0,
    paddingBottom: 8,
  },
  title: {
    fontSize: 17,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    flex: 1,
    minWidth: 0,
    letterSpacing: -0.02,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: '#FAFBFA',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  scroll: {
    maxHeight: 340,
  },
  deskScroll: {
    maxHeight: 360,
  },
  scrollContent: {
    paddingHorizontal: 18,
    paddingBottom: spacing.md,
    alignItems: 'center',
    gap: spacing.sm,
  },
  reasonList: {
    width: '100%',
    gap: 6,
  },
  reasonRow: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: '#FAFBFA',
  },
  reasonRowSelected: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  reasonLabel: {
    flex: 1,
    fontSize: 14,
    lineHeight: 19,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  helpLinkWrap: {
    marginTop: spacing.xs,
    textAlign: 'center',
  },
  helpHint: {
    fontSize: 13,
    fontWeight: '500',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  helpLink: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
    textDecorationLine: 'underline',
  },
  footer: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  btnCancel: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: '#FAFBFA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnCancelText: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  btnDanger: {
    flex: 1.4,
    height: 44,
    borderRadius: 10,
    backgroundColor: CONFIRM_WARN,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnDangerText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#FFFFFF',
    fontFamily: ticketFonts.sans,
  },
  btnDisabled: {
    opacity: 0.5,
  },
});
